/**
 * Tooltip — SSOT for every tooltip in the app.
 *
 * Usage:
 *   <Tooltip label="Open sidebar" placement="right">
 *     <button aria-label="Toggle sidebar">…</button>
 *   </Tooltip>
 *
 * Why a single component:
 *   Before this existed the app shipped three separate tooltip
 *   implementations:
 *     1. `.sidebar-icon-tooltip` — React state + `position: fixed`
 *     2. `.panel-toggle-tooltip` — same pattern, duplicated in ChatRoom
 *     3. `.sidebar.collapsed [title]:hover::after` — CSS-only
 *   Each had its own styling (colour, padding, shadow), its own
 *   clipping behaviour, and its own a11y story. This primitive
 *   collapses all three into one API backed by one CSS module.
 *
 * Positioning:
 *   Rendered via a portal to `document.body` with `position: fixed`
 *   so it always escapes overflow clipping regardless of the trigger's
 *   ancestor chain. The trigger's bounding-rect is measured on show.
 *
 * A11y:
 *   Triggered by mouse hover AND keyboard focus (focus-visible).
 *   The tooltip has `role="tooltip"` and is referenced by the trigger
 *   via `aria-describedby`. Touch devices skip the tooltip entirely
 *   (`@media (hover: none)`) because tap would fight the click action.
 *
 * Reduced motion:
 *   The fade-in is disabled under `prefers-reduced-motion: reduce`.
 */

import {
    cloneElement,
    useCallback,
    useEffect,
    useId,
    useRef,
    useState,
    type FocusEvent,
    type MouseEvent,
    type ReactElement,
    type RefCallback,
} from 'react';
import { createPortal } from 'react-dom';
import './Tooltip.css';

type Placement = 'top' | 'right' | 'bottom' | 'left';

type TooltipProps = {
    label: string;
    /** Anchor side relative to the trigger. Default 'right' matches the
     *  sidebar's established visual language. */
    placement?: Placement;
    /** Pixel gap between trigger edge and tooltip. Default 10. */
    offset?: number;
    /** Delay in ms before the tooltip appears. Default 300 — snappy but
     *  not triggered by mouse passing over a row. Set to 0 for immediate. */
    delayMs?: number;
    /** Single interactive child. The component clones it to attach
     *  event + ref + aria-describedby. */
    children: ReactElement;
};

type Position = { top: number; left: number } | null;

function computePosition(
    rect: DOMRect,
    placement: Placement,
    offset: number,
): Position {
    switch (placement) {
        case 'right':
            return { top: rect.top + rect.height / 2, left: rect.right + offset };
        case 'left':
            return { top: rect.top + rect.height / 2, left: rect.left - offset };
        case 'top':
            return { top: rect.top - offset, left: rect.left + rect.width / 2 };
        case 'bottom':
            return { top: rect.bottom + offset, left: rect.left + rect.width / 2 };
        default:
            return null;
    }
}

export default function Tooltip({
    label,
    placement = 'right',
    offset = 10,
    delayMs = 300,
    children,
}: TooltipProps) {
    const [pos, setPos] = useState<Position>(null);
    const tooltipId = useId();
    const triggerRef = useRef<HTMLElement | null>(null);
    const showTimerRef = useRef<number | null>(null);

    const cancelPending = useCallback(() => {
        if (showTimerRef.current !== null) {
            window.clearTimeout(showTimerRef.current);
            showTimerRef.current = null;
        }
    }, []);

    const show = useCallback(() => {
        cancelPending();
        const el = triggerRef.current;
        if (!el) return;
        const fire = () => {
            const rect = el.getBoundingClientRect();
            setPos(computePosition(rect, placement, offset));
        };
        if (delayMs <= 0) fire();
        else showTimerRef.current = window.setTimeout(fire, delayMs);
    }, [cancelPending, placement, offset, delayMs]);

    const hide = useCallback(() => {
        cancelPending();
        setPos(null);
    }, [cancelPending]);

    // Hide on scroll/resize — the fixed-position tooltip would otherwise
    // float in a stale location as the page moves under it.
    useEffect(() => {
        if (!pos) return;
        const onInvalidate = () => hide();
        window.addEventListener('scroll', onInvalidate, true);
        window.addEventListener('resize', onInvalidate);
        return () => {
            window.removeEventListener('scroll', onInvalidate, true);
            window.removeEventListener('resize', onInvalidate);
        };
    }, [pos, hide]);

    useEffect(() => cancelPending, [cancelPending]);

    // Merge our measurement ref with any function-ref the child already
    // carries. Object refs from forwardRef are rare on sidebar nav items
    // and mutating them from here is no longer allowed in React 19, so
    // we silently skip that case — if the rare consumer needs both an
    // object ref AND a tooltip, they can attach the tooltip externally.
    const childRef = (children as ReactElement & { ref?: unknown }).ref;
    const mergedRef: RefCallback<HTMLElement> = (node) => {
        triggerRef.current = node;
        if (typeof childRef === 'function') childRef(node);
    };

    type ChildHandlers = {
        onMouseEnter?: (e: MouseEvent<HTMLElement>) => void;
        onMouseLeave?: (e: MouseEvent<HTMLElement>) => void;
        onFocus?: (e: FocusEvent<HTMLElement>) => void;
        onBlur?: (e: FocusEvent<HTMLElement>) => void;
    };
    const originalProps = children.props as ChildHandlers;

    const trigger = cloneElement(children as ReactElement<Record<string, unknown>>, {
        ref: mergedRef,
        'aria-describedby': tooltipId,
        onMouseEnter: (e: MouseEvent<HTMLElement>) => {
            originalProps.onMouseEnter?.(e);
            show();
        },
        onMouseLeave: (e: MouseEvent<HTMLElement>) => {
            originalProps.onMouseLeave?.(e);
            hide();
        },
        onFocus: (e: FocusEvent<HTMLElement>) => {
            originalProps.onFocus?.(e);
            show();
        },
        onBlur: (e: FocusEvent<HTMLElement>) => {
            originalProps.onBlur?.(e);
            hide();
        },
    });

    return (
        <>
            {trigger}
            {pos && typeof document !== 'undefined'
                ? createPortal(
                    <div
                        id={tooltipId}
                        role="tooltip"
                        className={`ui-tooltip ui-tooltip--${placement}`}
                        style={{ top: pos.top, left: pos.left }}
                    >
                        {label}
                    </div>,
                    document.body,
                )
                : null}
        </>
    );
}
